import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { api } from '../lib/api';

/**
 * Worker health and the running-jobs counter, visible from every screen.
 *
 * A4 defines the slot and its states; A5 provides the data. Until a worker has
 * reported, the state is `unknown` — a worker that has not reported is not a
 * healthy worker, and the shell never pretends otherwise.
 */
export type WorkerState = 'unknown' | 'healthy' | 'stale' | 'down';

export interface SystemStatus {
  worker: { state: WorkerState; lastSeen?: string };
  /** null = unknown (no job source connected yet). */
  jobs: { running: number | null };
}

export const UNKNOWN_STATUS: SystemStatus = {
  worker: { state: 'unknown' },
  jobs: { running: null },
};

const SystemStatusContext = createContext<SystemStatus>(UNKNOWN_STATUS);

export function SystemStatusProvider({
  value = UNKNOWN_STATUS,
  children,
}: {
  value?: SystemStatus;
  children: ReactNode;
}) {
  return <SystemStatusContext.Provider value={value}>{children}</SystemStatusContext.Provider>;
}

/**
 * A5: the provider fed from GET /api/v1/system/status every 5 s (readable by every signed-in
 * role). Until the first answer — or when the API cannot say — the value stays UNKNOWN.
 */
export function SystemStatusFeed({ children }: { children: ReactNode }) {
  const q = useQuery({
    queryKey: ['system-status'],
    queryFn: api.systemStatus,
    refetchInterval: 5_000,
    retry: false,
  });
  const value = useMemo<SystemStatus>(() => {
    if (!q.data) return UNKNOWN_STATUS;
    return {
      worker: {
        state: q.data.worker.state,
        ...(q.data.worker.lastSeen ? { lastSeen: q.data.worker.lastSeen } : {}),
      },
      jobs: { running: q.data.migrated ? q.data.jobs.running : null },
    };
  }, [q.data]);
  return <SystemStatusProvider value={value}>{children}</SystemStatusProvider>;
}

export function useSystemStatus(): SystemStatus {
  return useContext(SystemStatusContext);
}
